import {useCallback} from 'react';
import {useModal} from 'react-modal-hook';
import cx from 'classnames';
import heic2any from 'heic2any';
import {Modal} from '~/GalleryPB/components/Modal/Modal';
import {Button} from '~library/atoms/Button';
import {CameraAddIcon} from '~library/svg/CameraAddIcon';
import {TrashIcon} from '~library/svg/TrashIcon';
import {Typography} from '~library/atoms/Typography';
import {RingSpinner} from '~library/atoms/RingSpinner';
import {useCacheGalleryImg} from '~/common/hooks/useCacheGalleryImg';
import {white} from '~libSass/base/_exports.sass';
import './PreviewPhotoUpload.sass';

const MAX_UPLOAD_SIZE_LIMIT = 1024 * 5; // 5MB
const ACCEPTED_FILE_MINE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];

export const PreviewPhotoUpload = () => {
  const {
    galleryImg,
    hasGalleryImg,
    storeAndUpdateImg,
    replaceAndUpdateImg,
    removeAndUpdateImg,
    loading,
  } = useCacheGalleryImg();

  const [showModal, hideModal] = useModal(() => (
    <Modal
      hideModal={hideModal}
      header="Your file could not be uploaded."
      description="Looks like your file is too large. Make sure your file is less than 5MB, then try again."
      ctaButtonText="Got it"
    />
  ));

  const [showFileExtModal, hideFileExtModal] = useModal(() => (
    <Modal
      hideModal={hideFileExtModal}
      header="Your file could not be uploaded."
      description="Looks like we don't accept that file type. Please try again with a PNG, JPEG, or HEIC."
      ctaButtonText="Got it"
    />
  ));

  const onImageChange = useCallback(
    async (fileEvent: React.ChangeEvent<HTMLInputElement>, replace: boolean) => {
      if (!fileEvent?.target?.files) return;

      let currentFile = fileEvent.target.files[0];
      if (!currentFile) return;

      if (!ACCEPTED_FILE_MINE_TYPES.includes(currentFile.type)) {
        showFileExtModal();
        return;
      }

      const reader = new FileReader();

      if (Math.round(currentFile.size / 1024) >= MAX_UPLOAD_SIZE_LIMIT) {
        showModal();

        // this enables the same file to be revalidate if uploaded
        // eslint-disable-next-line
        fileEvent.target.value = '';
        return;
      }

      if (currentFile.type === 'image/heic') {
        // fabric doesn't allow heic file types so...
        // convert heic to jpeg
        try {
          const blob = await heic2any({blob: currentFile, toType: 'image/jpeg', quality: 0.9});
          const jpegFile = new File([blob] as unknown as BlobPart[], 'image.jpeg', {
            type: 'image/jpeg',
          });
          currentFile = jpegFile;
        } catch (error) {
          evite.error('Conversion failed:', error);
          return;
        }
      }

      reader.addEventListener('load', async () => {
        const result = reader.result as string;
        if (replace) {
          await replaceAndUpdateImg(result);
        } else {
          await storeAndUpdateImg(result);
        }

        // this enables the same file to be revalidate if uploaded
        // eslint-disable-next-line
        fileEvent.target.value = '';
      });

      reader.onerror = (evt: ProgressEvent) => {
        evite.error('file onerror: ', evt);
      };

      reader.readAsDataURL(currentFile);
    },
    [showFileExtModal, showModal, replaceAndUpdateImg, storeAndUpdateImg]
  );

  const clearGalleryImage = useCallback(async () => {
    if (!galleryImg) return;

    await removeAndUpdateImg();
  }, [galleryImg, removeAndUpdateImg]);

  return (
    <div className={cx('preview-photo-upload-container', {grid: hasGalleryImg})}>
      <label
        htmlFor="preview-photo-upload"
        className={cx('preview-photo-upload-btn', {'full-width': !hasGalleryImg})}
      >
        {loading ? (
          <RingSpinner className="preview-photo-upload-spinner" color={white} />
        ) : (
          <CameraAddIcon ratio={0.75} color={white} />
        )}
        <Typography variant="paragraph2">
          {!hasGalleryImg ? 'Preview with my photo' : 'Replace photo'}
        </Typography>
      </label>
      <input
        type="file"
        id="preview-photo-upload"
        name="preview-photo-upload"
        accept={ACCEPTED_FILE_MINE_TYPES.join(',')}
        style={{display: 'none'}}
        onChange={(fileEvent) => onImageChange(fileEvent, hasGalleryImg)}
        data-qa-id={hasGalleryImg ? 'gallery-replace-btn' : 'gallery-upload-btn'}
        disabled={loading}
      />
      {!!hasGalleryImg && (
        <Button
          variant="secondary"
          className="remove-photo-btn"
          onClick={clearGalleryImage}
          data-qa-id="gallery-remove-btn"
          disabled={loading}
          disableProcessing
        >
          {loading ? (
            <RingSpinner className="preview-photo-upload-spinner" />
          ) : (
            <TrashIcon ratio={0.5} />
          )}
          <Typography variant="paragraph2" classNames="remove-photo-txt">
            Remove photo
          </Typography>
        </Button>
      )}
    </div>
  );
};
